/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 26-05-2026
 *
 * Algemene ballon-component voor dialogen binnen de topicons-box van een node.
 * Verzorgt de horizontale uitlijning t.o.v. het meegegeven anker-element.
 * Verticale positionering en zichtbaarheid blijven aan de ouder.
 * Inhoud wordt via ng-content (transclusie) aangeleverd.
 *
 * De uitlijning wordt opnieuw bepaald zodra de iconenrij van formaat verandert. Dat gebeurt terwijl
 * een ballon openstaat: de rij is gecentreerd op de node, dus zodra er een icoon bij komt of
 * verdwijnt — het normvraagteken komt een paar honderd milliseconden na een bewerking binnen —
 * schuiven álle iconen op. Zonder die waarneming bleef de ballon op zijn oude plek staan, meestal
 * bovenop het vraagteken, tot er toevallig iets anders een hertekening uitlokte.
 */

import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, signal } from '@angular/core';

@Component({
    selector: 'dl-node-dom-ballon',
    standalone: true,
    imports: [],
    templateUrl: './dl-node-dom-ballon.component.html',
    styleUrls: ['./dl-node-dom-ballon.component.scss']
})
export class DlNodeDomBallonComponent implements OnChanges, OnDestroy {

    /** Het anker-element (icoon/lampje) waarop de ballon horizontaal uitgelijnd wordt */
    @Input() anker: HTMLElement | null = null;

    /** Wordt aangeroepen wanneer de muis de ballon binnenkomt */
    @Output() hoverIn = new EventEmitter<void>();

    /** Wordt aangeroepen wanneer de muis de ballon verlaat */
    @Output() hoverOut = new EventEmitter<void>();

    /**
     * Bumpt zodra de iconenrij van formaat verandert. `dialoogLeft` leest hem, dus de binding in de
     * template is er een afnemer van en wordt opnieuw geëvalueerd — de meting zelf gebeurt niet in
     * de waarnemer, maar op het moment dat de weergave hem nodig heeft.
     */
    private readonly rijGewijzigd = signal(0);

    /**
     * De waarnemer op de iconenrij; opgeruimd bij een nieuw anker en bij het sluiten.
     */
    private rijwaarnemer: ResizeObserver | null = null;

    ngOnChanges(): void {
        this.volgDeRij();
    }

    ngOnDestroy(): void {
        this.rijwaarnemer?.disconnect();
        this.rijwaarnemer = null;
    }

    /**
     * Zet de waarnemer op de container waartegen de uitlijning gemeten wordt. Verandert die van
     * formaat, dan verschuift het anker erbinnen en klopt de berekende positie niet meer.
     */
    private volgDeRij(): void {
        this.rijwaarnemer?.disconnect();
        this.rijwaarnemer = null;
        const container = this.anker ? this.vindAbsoluteContainer(this.anker) : null;
        if (!container) {
            return;
        }
        this.rijwaarnemer = new ResizeObserver(() => this.rijGewijzigd.update(teller => teller + 1));
        this.rijwaarnemer.observe(container);
    }

    /**
     * Zoekt de eerstvolgende voorouder met position: absolute, te beginnen bij het opgegeven element.
     */
    private vindAbsoluteContainer(el: HTMLElement): HTMLElement | null {
        let c: HTMLElement | null = el.parentElement;
        while (c && window.getComputedStyle(c).position !== 'absolute') {
            c = c.parentElement;
        }
        return c;
    }

    /**
     * Berekent de left-waarde in pixels op basis van het anker-element en de absolute container.
     * Houdt rekening met de pijl-offset (19px) zoals in de bestaande styling.
     * Geeft '0px' terug als het anker of de container ontbreekt.
     */
    get dialoogLeft(): string {
        this.rijGewijzigd(); //opnieuw meten zodra de iconenrij van formaat verandert
        if (!this.anker) return '0px';
        const container = this.vindAbsoluteContainer(this.anker);
        if (!container) return '0px';
        const rLamp = this.anker.getBoundingClientRect();
        const rContainer = container.getBoundingClientRect();
        const left = ((rLamp.left + rLamp.width / 2) - rContainer.left) - 19;
        return Math.round(left) + 'px';
    }

    /** Vuurt het hoverIn-event af */
    opHoverIn(): void {
        this.hoverIn.emit();
    }

    /** Vuurt het hoverOut-event af */
    opHoverOut(): void {
        this.hoverOut.emit();
    }
}
